import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiX, FiCheck, FiAlertCircle, FiBookOpen, FiCalendar, FiAward } from 'react-icons/fi';
import apiService from '../../services/api';

const NotificationPanel = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background-color: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  width: 360px;
  max-height: 500px;
  z-index: 1000;
  overflow: hidden;
  
  @media (max-width: 768px) {
    right: -1rem;
    width: 320px;
    max-height: 400px;
  }
  
  @media (max-width: 480px) {
    width: 280px;
    right: -2rem;
  }
`;

const PanelHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MarkAllButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background-color: var(--background-tertiary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const NotificationList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: 300px;
  }
`;

const NotificationItem = styled(motion.div)`
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: var(--transition);
  position: relative;
  
  &:hover {
    background-color: var(--background-secondary);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.unread {
    background-color: rgba(216, 184, 137, 0.05);
    border-left: 3px solid var(--primary-color);
  }
`;

const NotificationIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
  
  &.info {
    background-color: rgba(96, 165, 250, 0.1);
    color: var(--info-color);
  }
  
  &.success {
    background-color: rgba(104, 211, 145, 0.1);
    color: var(--success-color);
  }
  
  &.warning {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-color);
  }
  
  &.error {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.h4`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
`;

const NotificationMessage = styled.p`
  color: var(--text-secondary);
  font-size: 0.8rem;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationTime = styled.span`
  color: var(--text-muted);
  font-size: 0.75rem;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
  }
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-dark);
    }
  }
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const UnreadIndicator = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 8px;
  height: 8px;
  background-color: var(--primary-color);
  border-radius: 0;
`;

function NotificationPanelComponent({ isOpen, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getNotifications();
      if (response.success) {
        setNotifications(response.notifications || []);
      } else {
        setError('Failed to fetch notifications');
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to fetch notifications');
      // For demo purposes, show some mock notifications
      setNotifications([
        {
          id: 1,
          title: 'New Course Content Added',
          message: 'Week 3 materials have been uploaded for AQ101 - Aqeedah Foundations',
          type: 'course_content',
          is_read: false,
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 2,
          title: 'Attendance Session Starting Soon',
          message: 'Week 2 attendance session for AQ101 will begin in 15 minutes',
          type: 'attendance',
          is_read: false,
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 3,
          title: 'Assignment Grade Posted',
          message: 'Your grade for Assignment 1 has been posted. Check your grades section.',
          type: 'grade',
          is_read: true,
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      // For demo purposes, mark as read locally
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      // For demo purposes, mark all as read locally
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      );
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'course_content':
        return <FiBookOpen size={16} />;
      case 'attendance':
        return <FiCalendar size={16} />;
      case 'grade':
        return <FiAward size={16} />;
      case 'announcement':
        return <FiAlertCircle size={16} />;
      default:
        return <FiBell size={16} />;
    }
  };

  const getNotificationIconClass = (type) => {
    switch (type) {
      case 'course_content':
        return 'info';
      case 'attendance':
        return 'warning';
      case 'grade':
        return 'success';
      case 'announcement':
        return 'error';
      default:
        return 'info';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <NotificationPanel
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          data-notification-panel
        >
          <PanelHeader>
            <PanelTitle>
              <FiBell size={16} />
              Notifications
              {unreadCount > 0 && (
                <span style={{ 
                  background: 'var(--primary-color)', 
                  color: 'var(--text-inverse)', 
                  fontSize: '0.75rem', 
                  padding: '0.125rem 0.375rem', 
                  borderRadius: 0,
                  marginLeft: '0.5rem'
                }}>
                  {unreadCount}
                </span>
              )}
            </PanelTitle>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {unreadCount > 0 && (
                <MarkAllButton onClick={markAllAsRead}>
                  <FiCheck size={14} />
                  Mark all read
                </MarkAllButton>
              )}
              <CloseButton onClick={onClose}>
                <FiX size={16} />
              </CloseButton>
            </div>
          </PanelHeader>

          <NotificationList>
            {loading ? (
              <LoadingState>
                <p>Loading notifications...</p>
              </LoadingState>
            ) : error ? (
              <EmptyState>
                <FiAlertCircle />
                <p>Failed to load notifications</p>
              </EmptyState>
            ) : notifications.length === 0 ? (
              <EmptyState>
                <FiBell />
                <p>No notifications yet</p>
              </EmptyState>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  className={!notification.is_read ? 'unread' : ''}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {!notification.is_read && <UnreadIndicator />}
                  
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <NotificationIcon className={getNotificationIconClass(notification.type)}>
                      {getNotificationIcon(notification.type)}
                    </NotificationIcon>
                    
                    <NotificationContent>
                      <NotificationTitle>{notification.title}</NotificationTitle>
                      <NotificationMessage>{notification.message}</NotificationMessage>
                      <NotificationTime>{formatTimeAgo(notification.created_at)}</NotificationTime>
                      
                      {!notification.is_read && (
                        <NotificationActions>
                          <ActionButton 
                            className="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                          >
                            <FiCheck size={12} />
                            Mark as read
                          </ActionButton>
                        </NotificationActions>
                      )}
                    </NotificationContent>
                  </div>
                </NotificationItem>
              ))
            )}
          </NotificationList>
        </NotificationPanel>
      )}
    </AnimatePresence>
  );
}

export default NotificationPanelComponent;
